/**
 * ScreeningPlanCard — „Моят план за проверките" (раздел Гърда).
 * Правила по UpToDate/ACS/USPSTF/ACR + програма за носителки на BRCA1/2.
 * Данни: GET /api/bhi/screening/plan, POST /exam, POST /profile
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarCheck, ShieldAlert, Dna, AlertTriangle, CheckCircle2, Clock,
  ChevronDown, Loader2, Plus, Calculator, Info, Pill,
} from "lucide-react";
import RiskScoreModal from "@/components/breast_intel/RiskScoreModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  overdue: { cls: "text-red-500", Icon: AlertTriangle },
  soon: { cls: "text-amber-500", Icon: Clock },
  ok: { cls: "text-emerald-500", Icon: CheckCircle2 },
  missing: { cls: "text-slate-400", Icon: Plus },
  future: { cls: "text-slate-400", Icon: Clock },
  info: { cls: "text-cyan-500", Icon: Clock },
};

const BRCA_OPTIONS = [
  { v: "unknown", l: "не знам / не е изследвано" },
  { v: "negative", l: "изследвана, без промяна" },
  { v: "brca1", l: "носител на BRCA1" },
  { v: "brca2", l: "носител на BRCA2" },
  { v: "vus", l: "промяна с неясно значение" },
];

const PROFILE_FACTORS = [
  { k: "chest_radiation_10_30", l: "Имала съм лъчелечение на гръдния кош между 10 и 30 г.",
    h: "самостоятелно определя висок риск — ЯМР от 30 г." },
  { k: "lcis_adh", l: "Имала съм биопсия с LCIS или атипична хиперплазия",
    h: "вдига категорията към умерен риск" },
  { k: "personal_bc_history", l: "Имала съм рак на гърдата или DCIS",
    h: "наблюдението се води от Вашия онколог" },
  { k: "rrso_done", l: "Яйчниците и тръбите ми са премахнати",
    h: "спира проследяването на яйчниците в плана" },
];

export default function ScreeningPlanCard({ isDark = false }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openBrca, setOpenBrca] = useState(false);
  const [openHarms, setOpenHarms] = useState(false);
  const [logFor, setLogFor] = useState(null);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [openHow, setOpenHow] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [openChemo, setOpenChemo] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bhi/screening/plan`);
      setD(data);
    } catch { setD(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveExam = async () => {
    if (!date) { toast.error("Изберете дата"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/bhi/screening/exam`,
        { modality: logFor, date });
      setD(data);
      setLogFor(null);
      setDate("");
      toast.success("Записано — следим срока");
    } catch { toast.error("Записването не успя"); } finally { setSaving(false); }
  };

  const setBrca = async (v) => {
    try {
      const { data } = await axios.post(`${API}/bhi/screening/profile`, { brca_status: v });
      setD(data);
      toast.success("Обновено");
    } catch { toast.error("Неуспешно"); }
  };

  const setFactor = async (k, v) => {
    try {
      const { data } = await axios.post(`${API}/bhi/screening/profile`, { [k]: v });
      setD(data);
    } catch { toast.error("Неуспешно"); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  if (loading) {
    return <div className={`p-4 rounded-2xl border ${card}`} data-testid="bhi-screening-loading">
      <Loader2 className="w-5 h-5 animate-spin text-pink-500 mx-auto" /></div>;
  }
  if (!d) return null;

  const catCls = d.category === "high" ? "text-red-500"
    : d.category === "moderate" ? "text-amber-500" : "text-emerald-500";

  return (
    <div className={`p-5 rounded-2xl border-2 ${card}`} data-testid="bhi-screening-plan-card">
      <div className="flex items-start gap-3">
        <CalendarCheck className="w-6 h-6 text-pink-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-extrabold">Моят план за проверките</h3>
          <p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Вашата категория риск: <span className={`font-bold ${catCls}`}
              data-testid="bhi-screening-category">{d.category_bg}</span>
            {" — "}{d.reasons_bg?.join("; ")}
          </p>
          <p className="text-xs mt-1 font-semibold text-pink-500" data-testid="bhi-screening-headline">
            {d.headline_bg}
          </p>
        </div>
      </div>

      {/* iter1015 — как се определя рискът + резултат от външен модел */}
      <div className={`mt-3 rounded-xl border ${isDark ? "border-white/10" : "border-slate-200"}`}
        data-testid="bhi-risk-how">
        <button onClick={() => setOpenHow((o) => !o)}
          className="w-full flex items-center justify-between gap-2 p-3 text-left"
          data-testid="bhi-risk-how-toggle">
          <span className="text-[12px] font-extrabold inline-flex items-center gap-1.5">
            <Info className="w-4 h-4 text-cyan-500" />
            Как се определя рискът ми
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${openHow ? "rotate-180" : ""}`} />
        </button>
        {openHow && d.risk_explainer && (
          <div className="px-3 pb-3 space-y-2 text-[11px]" data-testid="bhi-risk-how-body">
            <div className="opacity-85">{d.risk_explainer.method_bg}</div>
            <div className={`p-2.5 rounded-lg border-l-[3px] border-amber-500 ${
              isDark ? "bg-amber-500/10" : "bg-amber-50"}`} data-testid="bhi-risk-no-model">
              {d.risk_explainer.no_model_bg}
            </div>
            <div>
              <div className="font-bold opacity-60 uppercase text-[10px]">Прагове</div>
              {d.risk_explainer.thresholds_bg.map((t, i) => <div key={i}>• {t}</div>)}
            </div>
            <div>
              <div className="font-bold opacity-60 uppercase text-[10px]">
                Какво е отчетено при Вас
              </div>
              <div className="mt-1 space-y-1" data-testid="bhi-risk-factors">
                {d.risk_explainer.factors.map((f, i) => (
                  <div key={i} className="flex items-start gap-2">
                    {f.counted
                      ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                      : <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold">{f.label_bg}:</span>{" "}
                      <span className="opacity-85">{f.value_bg}</span>
                      <div className="text-[10px] opacity-55">{f.effect_bg}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="opacity-70">{d.risk_explainer.policy_bg}</div>
          </div>
        )}
      </div>

      {/* iter1016 — фактори, които бекендът чете, но досега нямаше къде да се въведат */}
      <div className={`mt-2 p-3 rounded-xl border ${
        isDark ? "border-white/10" : "border-slate-200"}`} data-testid="bhi-risk-factor-form">
        <div className="text-[11px] font-bold">Отметнете, ако се отнася за Вас</div>
        <div className="text-[10px] opacity-60 mb-1.5">
          Тези факти променят категорията и плана веднага.
        </div>
        <div className="space-y-1.5">
          {PROFILE_FACTORS.map((f) => (
            <label key={f.k} className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5 accent-pink-500"
                checked={!!d.profile_factors?.[f.k]}
                onChange={(e) => setFactor(f.k, e.target.checked)}
                data-testid={`bhi-factor-${f.k}`} />
              <span className="flex-1 min-w-0">
                <span className="text-[11px] font-semibold">{f.l}</span>
                <span className="block text-[10px] opacity-55">{f.h}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className={`mt-2 p-3 rounded-xl border-l-[3px] ${
        d.risk_source?.basis === "model"
          ? "border-rose-500 " + (isDark ? "bg-rose-500/10" : "bg-rose-50")
          : "border-slate-400 " + (isDark ? "bg-white/[0.04]" : "bg-slate-50")}`}
        data-testid="bhi-risk-source">
        <div className="text-[11px] font-bold inline-flex items-center gap-1">
          <Calculator className="w-3.5 h-3.5" /> Резултат от модел за оценка на риска
        </div>
        <div className="text-[11px] mt-0.5 opacity-85" data-testid="bhi-risk-source-note">
          {d.risk_source?.note_bg}
        </div>
        {d.external_score && (
          <div className="text-[11px] mt-0.5 font-semibold" data-testid="bhi-external-score">
            {d.external_score.model_label_bg}
            {[
              d.external_score.lifetime_risk != null
                ? `${d.external_score.lifetime_risk}% доживотен` : null,
              d.external_score.ten_year_risk != null
                ? `${d.external_score.ten_year_risk}% за 10 години` : null,
              d.external_score.five_year_risk != null
                ? `${d.external_score.five_year_risk}% за 5 години` : null,
              d.external_score.brca_carrier_prob != null
                ? `${d.external_score.brca_carrier_prob}% вероятност за носителство` : null,
              d.external_score.assessed_on,
            ].filter(Boolean).map((x) => ` · ${x}`).join("")}
            <div className="font-normal opacity-70">
              чете се по праговете на {d.external_score.thresholds_bg}
            </div>
          </div>
        )}
        <button onClick={() => setRiskOpen(true)}
          className="mt-2 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white
                     bg-gradient-to-r from-rose-500 to-pink-600 hover:opacity-95"
          data-testid="bhi-risk-score-open">
          {d.external_score ? "Обнови или добави резултат" : "Въведи резултат от модел"}
        </button>
      </div>

      <div className="mt-3 space-y-2" data-testid="bhi-screening-items">
        {d.plan.map((p) => {
          const st = STATUS[p.status] || STATUS.info;
          const { Icon } = st;
          return (
            <div key={p.code}
              className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-slate-200"}`}
              data-testid={`bhi-screening-item-${p.code}`}>
              <div className="flex items-start gap-2">
                <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${st.cls}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold">{p.title_bg}</div>
                  <div className="text-[11px] opacity-75 leading-snug">{p.why_bg}</div>
                  <div className={`text-[11px] mt-0.5 font-semibold ${st.cls}`}
                    data-testid={`bhi-screening-status-${p.code}`}>
                    {p.status_bg}
                  </div>
                </div>
                {p.code !== "supplemental" && (
                  <button onClick={() => { setLogFor(p.modality_key); setDate(""); }}
                    className="text-[11px] font-bold text-pink-500 hover:underline shrink-0"
                    data-testid={`bhi-screening-log-${p.code}`}>
                    Отбележи дата
                  </button>
                )}
              </div>
              {logFor === p.modality_key && (
                <div className="flex items-center gap-2 mt-2">
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className={`px-2.5 py-1.5 rounded-lg text-[12px] border ${
                      isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
                    data-testid="bhi-screening-date-input" />
                  <button onClick={saveExam} disabled={saving}
                    className="text-[12px] font-bold px-3 py-1.5 rounded-lg bg-pink-500 text-white
                               hover:bg-pink-600 disabled:opacity-50"
                    data-testid="bhi-screening-date-save">
                    Запази
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!!d.notes_bg?.length && (
        <ul className="mt-2 space-y-1" data-testid="bhi-screening-notes">
          {d.notes_bg.map((n, i) => (
            <li key={i} className="text-[11px] opacity-80">• {n}</li>
          ))}
        </ul>
      )}

      {/* генетичен тест */}
      <div className={`mt-3 p-3 rounded-xl border-l-[3px] ${
        d.genetic_testing.eligible ? "border-amber-500 bg-amber-500/10"
                                   : "border-emerald-500 bg-emerald-500/[0.08]"}`}
        data-testid="bhi-screening-genetic">
        <div className="text-[11px] font-bold inline-flex items-center gap-1">
          <Dna className="w-3.5 h-3.5" /> Генетичен тест
        </div>
        {!!d.genetic_testing.reasons_bg?.length && (
          <div className="text-[11px] mt-0.5 font-semibold">
            Основания: {d.genetic_testing.reasons_bg.join("; ")}
          </div>
        )}
        {(d.genetic_testing.carrier_prob != null || d.genetic_testing.dkg_score != null) && (
          <div className="text-[11px] mt-0.5" data-testid="bhi-genetic-combined">
            {[
              d.genetic_testing.dkg_score != null
                ? `DKG: ${d.genetic_testing.dkg_score} точки${
                    d.genetic_testing.dkg_tier_bg ? ` (${d.genetic_testing.dkg_tier_bg})` : ""}`
                : null,
              d.genetic_testing.carrier_prob != null
                ? `вероятност за носителство: ${d.genetic_testing.carrier_prob}% (праг 10%)`
                : null,
            ].filter(Boolean).join(" · ")}
            {d.genetic_testing.combined_note_bg && (
              <div className="mt-0.5 font-semibold">{d.genetic_testing.combined_note_bg}</div>
            )}
          </div>
        )}
        <div className="text-[11px] mt-0.5 opacity-85">{d.genetic_testing.advice_bg}</div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[10px] opacity-70">Моят BRCA статус:</span>
          <select value={d.brca_status || "unknown"} onChange={(e) => setBrca(e.target.value)}
            className={`px-2 py-1 rounded-lg text-[11px] border ${
              isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
            data-testid="bhi-screening-brca-select">
            {BRCA_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
      </div>

      {/* програма за носителки */}
      {d.brca_program && (
        <div className="mt-3 rounded-xl border-2 border-pink-500/40 bg-pink-500/[0.07]"
          data-testid="bhi-brca-program">
          <button onClick={() => setOpenBrca((o) => !o)}
            className="w-full flex items-center justify-between gap-2 p-3 text-left"
            data-testid="bhi-brca-program-toggle">
            <span className="text-[12px] font-extrabold inline-flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4 text-pink-500" />
              Програма за носителки на {d.brca_program.gene}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${openBrca ? "rotate-180" : ""}`} />
          </button>
          {openBrca && (
            <div className="px-3 pb-3 space-y-2 text-[11px]">
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Какъв е рискът</div>
                {d.brca_program.risks_bg.map((r, i) => <div key={i}>• {r}</div>)}
              </div>
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Наблюдение</div>
                {d.brca_program.surveillance_bg.map((r, i) => <div key={i}>• {r}</div>)}
              </div>
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Операции за намаляване на риска</div>
                {d.brca_program.surgery_bg.map((r, i) => <div key={i}>• {r}</div>)}
              </div>
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Лекарства за превенция</div>
                <div>{d.brca_program.chemoprevention_bg}</div>
              </div>
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Хормони след операция</div>
                <div>{d.brca_program.hormones_bg}</div>
              </div>
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Какво зависи от Вас</div>
                {d.brca_program.lifestyle_bg.map((r, i) => <div key={i}>• {r}</div>)}
              </div>
              <div className="font-semibold text-pink-500">{d.brca_program.next_step_bg}</div>
            </div>
          )}
        </div>
      )}

      {/* iter1016 — лекарство за намаляване на риска (5-годишен риск) */}
      {d.chemoprevention && (d.chemoprevention.eligible || d.chemoprevention.note_bg) && (
        <div className="mt-3 rounded-xl border-2 border-violet-500/40 bg-violet-500/[0.07]"
          data-testid="bhi-chemoprevention">
          <button onClick={() => setOpenChemo((o) => !o)}
            className="w-full flex items-center justify-between gap-2 p-3 text-left"
            data-testid="bhi-chemoprevention-toggle">
            <span className="text-[12px] font-extrabold inline-flex items-center gap-1.5">
              <Pill className="w-4 h-4 text-violet-500" />
              {d.chemoprevention.level === "strong"
                ? "Лекарство за намаляване на риска — обсъдете го скоро"
                : "Лекарство за намаляване на риска — има за какво да поговорите"}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${openChemo ? "rotate-180" : ""}`} />
          </button>
          {openChemo && (
            <div className="px-3 pb-3 space-y-2 text-[11px]" data-testid="bhi-chemoprevention-body">
              {!!d.chemoprevention.reasons_bg?.length && (
                <div className="font-semibold">
                  Защо се показва: {d.chemoprevention.reasons_bg.join("; ")}
                </div>
              )}
              <div className="opacity-75">{d.chemoprevention.threshold_bg}</div>
              {d.chemoprevention.note_bg && (
                <div className="p-2.5 rounded-lg border-l-[3px] border-amber-500 bg-amber-500/10">
                  {d.chemoprevention.note_bg}
                </div>
              )}
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Възможности</div>
                {d.chemoprevention.options_bg.map((x, i) => <div key={i}>• {x}</div>)}
              </div>
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Каква е ползата</div>
                {d.chemoprevention.benefits_bg.map((x, i) => <div key={i}>• {x}</div>)}
              </div>
              <div>
                <div className="font-bold opacity-60 uppercase text-[10px]">Какви са вредите</div>
                {d.chemoprevention.harms_bg.map((x, i) => <div key={i}>• {x}</div>)}
              </div>
              {d.chemoprevention.brca_note_bg && (
                <div className="opacity-85">{d.chemoprevention.brca_note_bg}</div>
              )}
              {d.chemoprevention.eligible && (
                <div className="font-semibold text-violet-500">
                  {d.chemoprevention.next_step_bg}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ползи и вреди */}
      <div className="mt-3">
        <button onClick={() => setOpenHarms((o) => !o)}
          className="text-[11px] font-bold opacity-70 hover:opacity-100 inline-flex items-center gap-1"
          data-testid="bhi-screening-harms-toggle">
          Ползи и вреди с числа
          <ChevronDown className={`w-3 h-3 transition-transform ${openHarms ? "rotate-180" : ""}`} />
        </button>
        {openHarms && (
          <div className={`mt-1.5 p-3 rounded-xl text-[11px] space-y-1 ${
            isDark ? "bg-white/[0.04]" : "bg-slate-50"}`} data-testid="bhi-screening-harms">
            <div className="opacity-70">{d.benefits_harms.note_bg}</div>
            <div>• {d.benefits_harms.annual_bg}</div>
            <div>• {d.benefits_harms.biennial_bg}</div>
            <div>• {d.benefits_harms.overdiagnosis_bg}</div>
            <div>• {d.benefits_harms.mortality_bg}</div>
          </div>
        )}
      </div>

      <div className="text-[9px] opacity-50 mt-3">{d.disclaimer_bg}</div>

      <RiskScoreModal open={riskOpen} onClose={() => setRiskOpen(false)}
        onSaved={(plan) => { if (plan) setD(plan); else load(); }} />
    </div>
  );
}
